import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FaUserShield, FaKey, FaPlus, FaSync, FaTrash, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/authService";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../components/LoadingOverlay";

/**
 * @file Security.js
 * @description Page for managing administrator access, roles, and credentials.
 */
export default function Security() { // Admin Access
    const { user } = useAuth();
    const navigate = useNavigate();

    // Access Control
    const isSuperAdmin = user?.role === "super_admin";

    const [admins, setAdmins] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [newAdmin, setNewAdmin] = useState({ username: "", email: "", password: "", role: "viewer", first_name: "", last_name: "" });
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination
    const [adminPage, setAdminPage] = useState(1);
    const itemsPerPage = 5;

    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [error, setError] = useState("");

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await authService.listUsers();
            // Ensure we extract an array. supporting { data: { users: [] } } (current), { data: [] }, or just []
            const list = Array.isArray(res?.data?.users) ? res.data.users : (Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
            setAdmins(list);
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Failed to list users", err);
            setAdmins([]); // Fallback to empty array on error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSuperAdmin) {
            loadUsers();
        }
    }, [isSuperAdmin]);

    if (!isSuperAdmin) {
        return (
            <div className="p-5 text-center">
                <FaUserShield size={50} className="text-muted mb-3" />
                <h4 className="fw-bold">Access Denied</h4>
                <p className="text-muted">You do not have permission to view this page.</p>
                <div className="text-muted small">Required Role: Super Admin</div>
                <div className="text-muted small">Current Role: {user?.role || "Unknown"}</div>
            </div>
        );
    }

    const handleCreate = async () => {
        if (!newAdmin.username || !newAdmin.password) {
            setError("Username and Password are required.");
            return;
        }
        setActionLoading(true);
        setError("");

        // Prepare payload
        const payload = {
            ...newAdmin,
            full_name: `${newAdmin.first_name || ""} ${newAdmin.last_name || ""}`.trim()
        };

        try {
            if (payload.role === 'super_admin') {
                // Use signup API for Super Admins
                await authService.signup(payload);
            } else {
                // Use standard create API for other roles
                await authService.createUser(payload);
            }

            await loadUsers();
            setShowModal(false);
            setNewAdmin({ username: "", email: "", password: "", role: "viewer", first_name: "", last_name: "" });
        } catch (err) {
            setError(err.message || "Failed to create user.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (username) => {
        if (!window.confirm(`Are you sure you want to delete user ${username}?`)) return;

        setActionLoading(true);
        try {
            await authService.deleteUser(username);
            await loadUsers();
        } catch (err) {
            alert(err.message || "Failed to delete user.");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="p-4 bg-light min-vh-100 position-relative">
            <LoadingOverlay loading={loading} />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">Settings</h4>
                    <p className="text-muted small mb-0">Manage administrator access and credentials.</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <div className="text-end d-none d-md-block me-3">
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
                        <div className="fw-bold small">{lastUpdated.toLocaleTimeString()}</div>
                    </div>
                    <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={loadUsers} disabled={loading || actionLoading}>
                        <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
                    </button>
                    <button className="btn btn-dark btn-sm px-3 d-flex align-items-center gap-2" onClick={() => setShowModal(true)}>
                        <FaPlus size={12} /> Create Admin
                    </button>
                </div>
            </div>

            {/* ADMIN TABLE */}
            <div className="card border-0 shadow-sm rounded-3">
                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0">Administrator Accounts</h6>
                    <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
                        <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
                            <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setAdminPage(prev => Math.max(prev - 1, 1))} disabled={adminPage === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                                <FaChevronLeft size={12} />
                            </button>
                            <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '40px', textAlign: 'center' }}>
                                {adminPage} / {Math.ceil(admins.length / itemsPerPage) || 1}
                            </span>
                            <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setAdminPage(prev => Math.min(prev + 1, Math.ceil(admins.length / itemsPerPage)))} disabled={adminPage >= Math.ceil(admins.length / itemsPerPage)} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                                <FaChevronRight size={12} />
                            </button>
                        </div>
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4 py-3">User</th>
                                <th>Role</th>
                                <th>Email</th>
                                <th className="pe-4 text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.length === 0 && !loading && (
                                <tr>
                                    <td colSpan="4" className="text-center py-4 text-muted">No users found.</td>
                                </tr>
                            )}
                            {admins.slice((adminPage - 1) * itemsPerPage, adminPage * itemsPerPage).map((admin, i) => (
                                <tr key={i}>
                                    <td className="ps-4">
                                        <div className="text-dark">{admin.first_name} {admin.last_name}</div>
                                        <div className="small text-muted font-monospace">@{admin.username}</div>
                                    </td>
                                    <td><span className="badge bg-light text-dark border text-uppercase">{admin.role}</span></td>
                                    <td className="text-muted small">{admin.email}</td>
                                    <td className="pe-4 text-end">
                                        {admin.username !== user.username && (
                                            <button className="btn btn-outline-danger btn-sm border-0" onClick={() => handleDelete(admin.username)} title="Delete User">
                                                <FaTrash />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* CREATE MODAL */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold h5">Create Admin Credentials</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        {error && <div className="alert alert-danger py-2 small">{error}</div>}
                        <div className="row">
                            <div className="col-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control type="text"
                                        value={newAdmin.first_name} onChange={e => setNewAdmin({ ...newAdmin, first_name: e.target.value })} />
                                </Form.Group>
                            </div>
                            <div className="col-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control type="text"
                                        value={newAdmin.last_name} onChange={e => setNewAdmin({ ...newAdmin, last_name: e.target.value })} />
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Username *</Form.Label>
                            <Form.Control type="text" required
                                value={newAdmin.username} onChange={e => setNewAdmin({ ...newAdmin, username: e.target.value })} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control type="email" placeholder="name@company.com"
                                value={newAdmin.email} onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Password *</Form.Label>
                            <Form.Control type="text" placeholder="Initial Password" required
                                value={newAdmin.password} onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Select value={newAdmin.role} onChange={e => setNewAdmin({ ...newAdmin, role: e.target.value })}>
                                <option value="viewer">Viewer (Read Only)</option>
                                <option value="super_admin">Super Admin (Full Access)</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="dark" onClick={handleCreate} disabled={actionLoading}>
                        {actionLoading ? "Creating..." : "Create User"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div >
    );
}
